'use client';
import React, { useState, useId } from 'react';
import PropTypes from 'prop-types';
import { t } from '../../i18n';
import classNames from 'classnames';
import { AnimateUpDown } from '../Animate';
import Richtext from '../Richtext';
import { useRevealable } from '../../utils/reveal';
import s from './AccordionItem.module.scss';

const OPENING_TIME = 300;

const AccordionItem = ({
    title = '',
    titleOpen = '',
    description = '',
    content,
    childrenTitle = '',
    size = 'small',
    modifier = '',
    blackHeaderBackground = false,
    open = false,
    children,
}) => {
    const [isExpanded, setIsExpanded] = useState(open);
    const id = useId();
    const contentRef = useRevealable(isExpanded, setIsExpanded, OPENING_TIME);

    const clickHandler = () => setIsExpanded(!isExpanded);

    const headerId = `${id}-header`;

    const buttonText = isExpanded
        ? t('accordionItem.minimize')
        : t('accordionItem.expand');

    const classes = classNames(
        s.Root,
        [s[`Root--${modifier}`]],
        { [s['Root--Expanded']]: isExpanded },
        { [s['Root--Small']]: size === 'small' },
        { [s['Root--Large']]: size === 'large' },
        {
            [s['Root--BlackHeaderBackground']]:
                blackHeaderBackground && isExpanded,
        },
        { [s['Root--ChildrenTitle']]: childrenTitle }
    );

    if (!title && !description) {
        return null;
    }

    const contentRichTextModifier =
        size == 'small' ? 'SmallText' : 'MediumText';

    return (
        <div className={classes}>
            <div
                className={s.Header}
                onClick={clickHandler}
                aria-expanded={isExpanded}
                aria-controls={id}
                id={headerId}>
                <div className={s.TextWrapper}>
                    <h3 className={s.Title}>
                        {isExpanded && titleOpen ? titleOpen : title}
                    </h3>
                    {description && (
                        <span className={s.Description}>{description}</span>
                    )}
                </div>
                <button
                    className={s.Button}
                    type="button"
                    aria-label={buttonText}>
                    <span className={s.Icon} />
                </button>
            </div>
            <AnimateUpDown isVisible={isExpanded} duration={OPENING_TIME}>
                <div
                    ref={contentRef}
                    className={s.ChildrenWrapper}
                    aria-hidden={!isExpanded}
                    aria-labelledby={headerId}
                    id={id}>
                    {childrenTitle && (
                        <div className={s.ChildrenTitle}>{childrenTitle}</div>
                    )}
                    {!children && (
                        <Richtext
                            modifier={contentRichTextModifier}
                            text={content}
                        />
                    )}
                    {!content && children}
                </div>
            </AnimateUpDown>
        </div>
    );
};

AccordionItem.propTypes = {
    title: PropTypes.string.isRequired,
    titleOpen: PropTypes.string,
    description: PropTypes.string,
    open: PropTypes.bool,
    children: PropTypes.node,
    size: PropTypes.oneOf(['small', 'large']),
    modifier: PropTypes.string,
    blackHeaderBackground: PropTypes.bool,
};

export default AccordionItem;
